import React, { FC, useEffect, useState } from "react";

import {
	Button,
	ConfirmModal,
	Dropdown,
	Grid,
	SectionCard,
	TMenuItem,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ViewsService from "@/services/ViewsService";

import { useMonthPeriods } from "@/hooks/useMonthPeriods";
import { useViewOverview } from "@/hooks/useOverview";
import { useViewActions } from "@/hooks/useViewActions";

import { StatTile } from "@/components/molecules";
import {
	ViewFormModal,
	LatestTasks,
	ProjectFormModal,
	ProjectsCard,
} from "@/components/organisms";
import { PageLayout } from "@/components/templates";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { hoursDelta } from "@/utils/UAnalytics";

import { viewKeys } from "@/constants/CQueryKeys";

import { ViewBanner } from "./components/ViewBanner";

const euros = (amount: number) =>
	new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
	}).format(amount);

/**
 * `/views/:id` - one view: its banner, this month's figures (with the
 * change against last month) and the projects it gathers. Editing the
 * view itself (name, colour, selection) goes through the same modal as
 * the list; lock and delete sit behind the "more" menu. `?edit=1` opens
 * that modal and `?new=1` the new-project one (the sidebar menus land
 * here with them); both params are consumed on arrival.
 */
export const ViewDetailView: FC = () => {
	const { id: idParam } = useParams<{ id: string }>();
	const id = Number(idParam);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { archive, remove, includeProject } = useViewActions();
	const { current } = useMonthPeriods();

	const [isEditing, setIsEditing] = useState(false);
	const [isCreatingProject, setIsCreatingProject] = useState(false);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	useEffect(() => {
		const wantsEdit = searchParams.get("edit") === "1";
		const wantsNew = searchParams.get("new") === "1";
		if (!wantsEdit && !wantsNew) return;
		if (wantsEdit) setIsEditing(true);
		if (wantsNew) setIsCreatingProject(true);
		// The params did their job; a reload must not reopen the modals.
		const next = new URLSearchParams(searchParams);
		next.delete("edit");
		next.delete("new");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	const {
		data: view,
		isLoading,
		error,
	} = useQuery({
		queryKey: viewKeys.detail(id),
		queryFn: () => ViewsService.getView(id),
		enabled: !Number.isNaN(id),
	});

	// This month, the previous one and the total, in one answer.
	const overview = useViewOverview(view ? id : 0);
	const figures = overview.data;

	// A view is personal: its owner does everything on it.
	const canEdit = true;
	const canAdmin = true;

	const menuItems: TMenuItem[] = view
		? [
				{
					label: view.isArchived
						? t("actions.unarchive")
						: t("actions.archive"),
					picto: "archive",
					hidden: !canEdit,
					onClick: () => void archive(view),
				},
				{
					label: t("actions.delete"),
					picto: "trash",
					color: "danger",
					hidden: !canAdmin,
					onClick: () => setIsConfirmingDelete(true),
				},
			]
		: [];

	const expectedRaw = figures?.period.expectedHours.raw ?? 0;
	const workedRaw = figures?.period.workedHours.raw ?? 0;
	// The max is what the period pays when every expected hour is worked;
	// what is billable so far is that max prorated on the hours done.
	const maxSalary = figures?.period.maxEstimatedSalary ?? 0;
	const currentSalary =
		expectedRaw > 0 ? maxSalary * (workedRaw / expectedRaw) : 0;

	return (
		<>
			<PageLayout
				title={view?.name ?? t("views.label")}
				isLoading={isLoading}
				error={
					error
						? getApiErrormessage(error)
						: !view
							? t("views.notFound")
							: false
				}
				onBack={() => navigate("/views")}
				onBackLabel={t("views.back")}
				actions={[
					canEdit && (
						<Button
							key="edit"
							variant="outline"
							picto="pen"
							onClick={() => setIsEditing(true)}
						>
							{t("actions.edit")}
						</Button>
					),
					menuItems.some((item) => !item.hidden) && (
						<Dropdown
							key="more"
							items={menuItems}
							placement="bottom-end"
						>
							<Button
								variant="outline"
								picto="more"
								aria-label={t("actions.more")}
							/>
						</Dropdown>
					),
				].filter(Boolean)}
			>
				{!!view && (
					<div className="flex flex-col gap-6">
						<ViewBanner view={view} />

						<Grid
							columns={4}
							minItemWidth="300px"
							data-testid="view-stats"
						>
							<StatTile
								label={`${t("views.stats.worked")} · ${t("views.stats.thisMonth")}`}
								value={figures?.period.workedHours.str ?? "—"}
								delta={hoursDelta(
									figures?.period,
									figures?.previous
								)}
								sub={t("views.stats.vsPreviousMonth")}
								isLoading={overview.isLoading}
							/>
							<StatTile
								label={t("views.stats.expected")}
								value={figures?.period.expectedHours.str ?? "—"}
								progress={
									expectedRaw > 0
										? (workedRaw / expectedRaw) * 100
										: 0
								}
								isLoading={overview.isLoading}
							/>
							<StatTile
								label={t("views.stats.tasks")}
								value={figures?.period.tasksCount ?? "—"}
								sub={t("views.stats.thisMonth")}
								isLoading={overview.isLoading}
							/>
							<StatTile
								label={t("views.stats.salary")}
								value={figures ? euros(currentSalary) : "—"}
								// The rate comes from the projects' clients now:
								// a figure only when they all agree on one.
								sub={
									maxSalary
										? t("views.stats.ofMax", {
												amount: euros(maxSalary),
											})
										: t("projects.stats.noRate")
								}
								isLoading={overview.isLoading}
							/>
						</Grid>

						<Grid columns={2} minItemWidth="400px" gap="1.5rem">
							<ProjectsCard
								scope="views"
								id={view.id}
								period={current}
								canCreate={canEdit && !view.isArchived}
								onCreate={() => setIsCreatingProject(true)}
								className="max-h-100"
							/>
							<LatestTasks view={view} />
							{canEdit && (
								<SectionCard
									title={
										view.isArchived
											? t("views.dangerZone.unarchive")
											: t("views.dangerZone.archive")
									}
									className="border-danger-200"
									actions={
										<Button
											size="sm"
											variant="outline"
											color={
												view.isArchived
													? "neutral"
													: "danger"
											}
											picto="archive"
											onClick={() => void archive(view)}
										>
											{view.isArchived
												? t("actions.unarchive")
												: t("actions.archive")}
										</Button>
									}
								>
									<p className="text-sm text-neutral-500">
										{view.isArchived
											? t(
													"views.dangerZone.unarchiveHint"
												)
											: t("views.dangerZone.archiveHint")}
									</p>
								</SectionCard>
							)}
						</Grid>
					</div>
				)}
			</PageLayout>
			{!!view && (
				<>
					<ViewFormModal
						open={isEditing}
						view={view}
						onClose={() => setIsEditing(false)}
					/>
					<ProjectFormModal
						open={isCreatingProject}
						onClose={() => setIsCreatingProject(false)}
						onSaved={(project) => {
							void includeProject(view, project);
							navigate(`/projects/${project.id}`);
						}}
					/>
					<ConfirmModal
						open={isConfirmingDelete}
						onClose={() => setIsConfirmingDelete(false)}
						size="sm"
						title={t("views.confirmDelete.title")}
						onConfirm={() => {
							setIsConfirmingDelete(false);
							void remove(view).then(() => navigate("/views"));
						}}
						confirmText={t("actions.delete")}
						confirmColor="danger"
						cancelText={t("actions.cancel")}
					>
						<Trans
							i18nKey="views.confirmDelete.message"
							values={{ name: view.name }}
							components={{ b: <b /> }}
						/>
					</ConfirmModal>
				</>
			)}
		</>
	);
};
